/**
 * StoreCard — Store list item with icon, name, status, details, action
 *
 * Used for: store lists on home/browse screens
 * When bannerUrl is provided: renders as full-bleed image card with gradient overlay
 * Otherwise: renders as compact horizontal list item
 */
import type { ReactNode } from 'react'
import { Image, ImageBackground } from 'react-native'
import { LinearGradient } from 'expo-linear-gradient'
import { styled, XStack, YStack, Text as TamaguiText, type GetProps } from 'tamagui'

const StoreCardFrame = styled(XStack, {
  name: 'StoreCard',
  ai: 'center',
  bg: '$backgroundCard',
  br: '$lg',
  p: 14,
  gap: '$sm',
  cursor: 'pointer',
  animation: 'quick',
  animateOnly: ['opacity', 'transform'],
  pressStyle: { opacity: 0.85, scale: 0.98 },
  hoverStyle: { opacity: 0.92 },
})

const StoreIconWrap = styled(XStack, {
  name: 'StoreIconWrap',
  w: 44,
  h: 44,
  br: '$lg',
  bg: '$colorPrimaryMuted',
  ai: 'center',
  jc: 'center',
})

const StoreInfo = styled(YStack, {
  name: 'StoreInfo',
  f: 1,
})

const StoreNameRow = styled(XStack, {
  name: 'StoreNameRow',
  ai: 'center',
  gap: '$sm',
})

const StoreName = styled(TamaguiText, {
  name: 'StoreName',
  fontFamily: '$body',
  fontSize: 14,
  fontWeight: '600',
  color: '$color',
})

const StatusDot = styled(YStack, {
  name: 'StatusDot',
  w: 8,
  h: 8,
  br: '$full',

  variants: {
    status: {
      open: { bg: '$colorPrimary' },
      closed: { bg: '$colorSecondary' },
    },
  } as const,

  defaultVariants: {
    status: 'closed',
  },
})

const StoreDetails = styled(TamaguiText, {
  name: 'StoreDetails',
  fontFamily: '$body',
  fontSize: 12,
  color: '$colorSecondary',
  mt: 2,
})

type StoreCardFrameProps = GetProps<typeof StoreCardFrame>

interface StoreCardProps extends StoreCardFrameProps {
  icon?: ReactNode
  imageUrl?: string | null
  bannerUrl?: string | null
  name: string
  isOpen?: boolean
  details?: string
  right?: ReactNode
  onPress?: () => void
}

export const StoreCard = StoreCardFrame.styleable<StoreCardProps>(
  ({ icon, imageUrl, bannerUrl, name, isOpen, details, right, onPress, ...props }, ref) => {
    if (bannerUrl) {
      return (
        <XStack
          br="$lg"
          overflow="hidden"
          h={110}
          cursor="pointer"
          animation="quick"
          animateOnly={['opacity', 'transform']}
          pressStyle={{ opacity: 0.85, scale: 0.98 }}
          hoverStyle={{ opacity: 0.92 }}
          onPress={onPress}
        >
          <ImageBackground
            source={{ uri: bannerUrl }}
            style={{ width: '100%', height: '100%' }}
            resizeMode="cover"
          >
            <LinearGradient
              colors={['transparent', 'rgba(0,0,0,0.62)']}
              style={{ flex: 1, justifyContent: 'space-between', padding: 12 }}
            >
              {/* top-right slot */}
              <XStack jc="flex-end">
                {right}
              </XStack>
              {/* bottom: name + details */}
              <YStack gap={2}>
                <XStack ai="center" gap={6}>
                  <TamaguiText fontFamily="$body" fontSize={15} fontWeight="700" color="white">
                    {name}
                  </TamaguiText>
                  {isOpen !== undefined && (
                    <StatusDot status={isOpen ? 'open' : 'closed'} />
                  )}
                </XStack>
                {details && (
                  <TamaguiText fontFamily="$body" fontSize={12} color="rgba(255,255,255,0.75)">
                    {details}
                  </TamaguiText>
                )}
              </YStack>
            </LinearGradient>
          </ImageBackground>
        </XStack>
      )
    }

    return (
      <StoreCardFrame ref={ref} onPress={onPress} {...props}>
        <StoreIconWrap>
          {imageUrl ? (
            <Image
              source={{ uri: imageUrl }}
              style={{ width: 44, height: 44, borderRadius: 10 }}
              resizeMode="cover"
            />
          ) : icon}
        </StoreIconWrap>
        <StoreInfo>
          <StoreNameRow>
            <StoreName>{name}</StoreName>
            {isOpen !== undefined && (
              <StatusDot status={isOpen ? 'open' : 'closed'} />
            )}
          </StoreNameRow>
          {details && <StoreDetails>{details}</StoreDetails>}
        </StoreInfo>
        {right}
      </StoreCardFrame>
    )
  },
)
